import React from "react";
import { SCHEDULED_ONLY_NOTICE } from "../utils/deliverySchedule";
import { formatNextOpening } from "../utils/vendorOpenState";

/**
 * RestaurantAvailabilityBanner — the single, honest statement of whether this
 * restaurant is taking orders.
 *
 * It renders four visually distinct states, because collapsing them is what
 * made the storefront confusing: "closed" (a settled fact the shopper can plan
 * around), "hours unavailable" (our problem, retryable), "checking" (transient,
 * must never look like closed) and open (renders nothing).
 *
 * A fifth, quieter variant covers "hours not confirmed": the check failed but
 * the vertical (retail, brand, healthcare) is open by policy until hours are
 * published, so `canOrder` stays true. It is informational (role status, muted
 * grey-blue) rather than a warning, and still offers Retry.
 *
 * The closed variant is deliberately the loudest thing on the page — a shopper
 * should never get as far as a filled cart before learning the kitchen is shut.
 */
const RestaurantAvailabilityBanner = ({
  availability,
  hideWhenOpen = true,
  compact = false,
}) => {
  if (!availability) return null;

  // Closed now, but this vendor takes orders for later. Calm and informational:
  // the shopper can keep browsing and adding to cart, and checkout asks for a
  // delivery time.
  if (availability.scheduledOrdersOnly) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          padding: compact ? "10px 12px" : "12px 16px",
          border: "1px solid #bfe3cd",
          borderRadius: 10,
          background: "#eef7f1",
          color: "#0a5c40",
          fontSize: compact ? 13 : 14,
          lineHeight: 1.45,
          margin: compact ? "8px 0" : "10px 0",
        }}
      >
        <strong style={{ display: "block", fontSize: compact ? 13 : 15 }}>
          {SCHEDULED_ONLY_NOTICE}
        </strong>
        {formatNextOpening(availability.nextOpening) && (
          <span style={{ display: "block", marginTop: 2, opacity: 0.92 }}>
            {formatNextOpening(availability.nextOpening)}
          </span>
        )}
      </div>
    );
  }

  const isChecking =
    availability.isChecking ??
    (availability.status === "loading" || availability.status === "idle");
  const isError = availability.isUnknown ?? availability.status === "error";
  // Unknown-but-trading: the hours check failed, but this vertical keeps
  // trading by policy, so canOrder is true and the note is informational.
  const isUnconfirmed = isError && availability.tradesWhileUnknown === true;
  const isClosed = !isChecking && !isError;

  // Open and confirmed: nothing to say. The unconfirmed variant must survive
  // this check because canOrder is also true in that state.
  if (hideWhenOpen && availability.canOrder && !isUnconfirmed) return null;

  const palette = isChecking
    ? { bg: "#f4f7f8", border: "#cad4d8", ink: "#37474f", accent: "#78909c" }
    : isUnconfirmed
      ? { bg: "#EEF3F7", border: "#B8C8CE", ink: "#37474f", accent: "#B8C8CE" }
      : isError
        ? { bg: "#fff7e6", border: "#e4a11b", ink: "#6b4a00", accent: "#e4a11b" }
        : { bg: "#fff2ef", border: "#d64535", ink: "#7d1f17", accent: "#d64535" };

  const title = isChecking
    ? "Checking hours"
    : isUnconfirmed
      ? "Hours not confirmed"
      : isError
        ? "Hours unavailable"
        : "Closed right now";

  return (
    <div
      role={isChecking || isUnconfirmed ? "status" : "alert"}
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: compact ? "center" : "flex-start",
        justifyContent: "space-between",
        gap: 12,
        padding: compact ? "10px 12px" : "14px 16px",
        border: `1px solid ${palette.border}`,
        borderLeft: `${isClosed && !compact ? 5 : 1}px solid ${palette.accent}`,
        borderRadius: 10,
        background: palette.bg,
        color: palette.ink,
        fontSize: compact ? 13 : 14,
        lineHeight: 1.45,
        margin: compact ? "8px 0" : "10px 0",
      }}
    >
      <span>
        <strong
          style={{
            display: "block",
            fontSize: isClosed && !compact ? 16 : compact ? 13 : 15,
            letterSpacing: isClosed ? "0.01em" : undefined,
          }}
        >
          {title}
        </strong>
        <span style={{ display: "block", marginTop: 2, opacity: 0.92 }}>
          {availability.message}
        </span>
      </span>
      {(isError || isChecking) && (
        <button
          type="button"
          onClick={() => availability.refresh()}
          disabled={isChecking}
          style={{
            border: `1px solid ${palette.border}`,
            borderRadius: 7,
            background: "#fff",
            color: palette.ink,
            padding: "6px 10px",
            cursor: isChecking ? "wait" : "pointer",
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {isChecking ? "Checking…" : "Retry"}
        </button>
      )}
    </div>
  );
};

export default RestaurantAvailabilityBanner;
